import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import OptionRow from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sidebars/FieldSidebar/OptionRow';
import type FieldDraftFormData from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/FieldDraftFormData';
import { css } from '@emotion/react';
import * as React from 'react';
import { Grid, GridItem } from '@verclam/grid-system';
import { Button, FieldShell, InputText } from '@verclam/ui-kit';

/** The shell already carries the field margin: the controls nested in it must not add theirs. */
const optionsStyle = css`
	.ui-kit-field {
		margin: 0;
	}
`;

type Props = {
	readonly formData: FieldDraftFormData;
	readonly onChange: (update: (formData: FieldDraftFormData) => FieldDraftFormData) => void;
};

/** The options of a choice field: the existing ones, each removable unless saved on a used type, and the way to add one. */
const OptionsEditor: React.FC<Props> = ({ formData, onChange }) => {
	const [newOption, setNewOption] = React.useState('');
	const errorMessage = formData.getValidationMessage('options');

	const handleAddClick = React.useCallback(() => {
		onChange((current) => current.addOption(newOption));
		setNewOption('');
	}, [onChange, newOption]);

	const handleNewOptionKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				handleAddClick();
			}
		},
		[handleAddClick],
	);

	const handleRemove = React.useCallback(
		(index: number) => {
			onChange((current) => current.removeOption(index));
		},
		[onChange],
	);

	return (
		<ConditionalLoader data={formData.isChoice}>
			<FieldShell
				name="options"
				label="Options"
				required
				errorMessage={errorMessage}
				isFormFieldValid={formData.isValid('options')}
			>
				<div css={optionsStyle}>
					<Grid columns={12} columnGap={1} rowGap={0.5} alignItems="center">
						{formData.options.map((option, index) => (
							<OptionRow
								// biome-ignore lint/suspicious/noArrayIndexKey: an option being typed has no identity besides its rank
								key={index}
								index={index}
								label={option.label}
								isRemovable={!formData.isOptionLocked(index)}
								onRemove={handleRemove}
							/>
						))}
						<GridItem columnSize={{ xs: 9, xl: 9 }}>
							<InputText
								name="newOption"
								label="Nouvelle option"
								isLabelHidden
								placeholder="Nouvelle option…"
								value={newOption}
								onChange={setNewOption}
								onKeyDown={handleNewOptionKeyDown}
							/>
						</GridItem>
						<GridItem columnSize={{ xs: 3, xl: 3 }}>
							<Button
								type="button"
								size="sm"
								severity="secondary"
								label="Ajouter"
								disabled={newOption.trim().length === 0}
								onClick={handleAddClick}
							/>
						</GridItem>
					</Grid>
				</div>
			</FieldShell>
		</ConditionalLoader>
	);
};

export default OptionsEditor;
